import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Download, Eye, Edit, User } from 'lucide-react';
import { usePagination, useCrud, useApiQuery } from '../../hooks/useApi';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { identityService, contactService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { unwrapList } from '../../utils/entityIds';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import PersonModal from '../../components/Identity/PersonModal';
import PersonTable from '../../components/Identity/PersonTable';
import PersonFilters from '../../components/Identity/PersonFilters';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { formatCpf } from '../../utils/cpf';

const Persons = () => {
  const { t } = useTranslation('identity');

  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  
  const pagination = usePagination(1, 10);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    sortBy: 'name',
  });
  
  // Adapter para o serviço esperado por useCrud
  const personApi = {
    getList: (params = {}) => identityService.getPersons(params).then(res => res.data),
    getItem: (id) => identityService.getPerson(id).then(res => res.data),
    create: (data) => identityService.createPerson(data).then(res => res.data),
    update: (id, data) => identityService.updatePerson(id, data).then(res => res.data),
    delete: (id) => identityService.deletePerson(id).then(res => res.data),
  };
  
  const { useCreate, useUpdate, useDelete } = useCrud('persons', personApi);
  const { data: personsRaw = [], isLoading, error } = useApiQuery('persons-all-pages', () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();
  const allPersons = unwrapList(personsRaw);

  const filteredPersons = useMemo(() => {
    let rows = allPersons;
    const q = String(filters.search || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((p) =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.cpf || p.document || '').toLowerCase().includes(q)
      );
    }
    if (filters.status === 'ACTIVE') rows = rows.filter((p) => p.is_active === true);
    if (filters.status === 'INACTIVE') rows = rows.filter((p) => p.is_active === false);
    const sorted = [...rows].sort((a, b) => {
      const key = filters.sortBy === 'document' ? (a.cpf || a.document || '') : (a.name || '');
      const other = filters.sortBy === 'document' ? (b.cpf || b.document || '') : (b.name || '');
      return String(key).localeCompare(String(other));
    });
    return sorted;
  }, [allPersons, filters]);

  useEffect(() => {
    pagination.setTotal(filteredPersons.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPersons.length]);

  const persons = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filteredPersons.slice(start, start + pagination.limit);
  }, [filteredPersons, pagination.page, pagination.limit]);

  const transformPayload = (input) => {
    const payload = {
      name: input?.name,
      is_active: input?.status === 'ACTIVE',
      address: input?.address,
      city: input?.city,
      state: input?.state,
      zip_code: input?.zip_code,
      email: input?.email?.trim() || undefined,
    };

    const cpf = formatCpf(input?.cpf);
    if (cpf) {
      payload.cpf = cpf;
    }

    return payload;
  };

  const personToInvitePayload = (input) => ({
    name: input?.name?.trim(),
    cpf: formatCpf(input?.cpf) || undefined,
    email: input?.email?.trim() || undefined,
    address: input?.address?.trim() || undefined,
    city: input?.city?.trim() || undefined,
    state: input?.state?.trim() || undefined,
    zip_code: input?.zip_code?.trim() || undefined,
  });
  
  const handleCreatePerson = () => {
    setSelectedPerson(null);
    setShowModal(true);
  };
  
  const handleEditPerson = async (person) => {
    try {
      const { data } = await identityService.getPerson(person.id);
      setSelectedPerson({ ...person, ...(data || {}) });
    } catch {
      setSelectedPerson(person);
    }
    setShowModal(true);
  };
  
  const handleViewPerson = (person) => {
    console.log('View person:', person);
  };
  
  const handleDeletePerson = (person) => {
    if (window.confirm('Tem certeza que deseja excluir esta pessoa?')) {
      deleteMutation.mutate(person.id);
    }
  };
  
  const getStatusBadge = (isActive) => {
    return (
      <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>
        {isActive ? 'Ativo' : 'Inativo'}
      </span>
    );
  };
  
  const getPersonTypeBadge = () => {
    return (
      <span className="badge badge-primary">
        Pessoa Física
      </span>
    );
  };
  
  const resolvePersonType = () => {
    // Sempre retorna individual (pessoa física)
    return 'individual';
  };

  const resolveDocument = (p) => {
    // Primeiro tenta o campo document direto
    if (p?.document) return p.document;
    
    // Sempre retorna CPF (pessoa física)
    return p?.cpf || '';
  };
  const resolveIsActive = (p) => {
    // Usa diretamente is_active do backend
    return p?.is_active === true;
  };
  const resolveCreatedAt = (p) => p?.createdAt || p?.created_at || new Date().toISOString();

  // Função para salvar contatos via ContactService
  const saveContacts = async (personId, contacts) => {
    if (!contacts || contacts.length === 0) return;
    
    try {
      console.log('Salvando contatos para pessoa:', personId, contacts);
      
      // Estratégia upsert simples: apaga e recria
      const existing = await contactService.getContactsByPerson(personId);
      const existingContacts = existing.data || [];

      // Deletar em paralelo para acelerar
      if (existingContacts.length > 0) {
        await Promise.all(existingContacts.map((c) => contactService.deleteContact(c.id)));
      }

      // Criar novos contatos em paralelo
      if (contacts.length > 0) {
        await Promise.all(
          contacts.map((c) =>
            contactService.createContact({
              person_id: personId,
              contact_type: String(c.contact_type || '').toLowerCase(),
              value: c.value,
              description: c.description,
              is_primary: !!c.is_primary,
            })
          )
        );
      }
      
      console.log('Contatos salvos com sucesso');
    } catch (error) {
      console.error('Erro ao salvar contatos:', error);
      throw error;
    }
  };

  // Função para carregar contatos do ContactService
  const resolveContacts = async (p) => {
    if (!p?.id) {
      console.log('resolveContacts: Sem ID da pessoa');
      return [];
    }
    
    try {
      console.log('resolveContacts: Carregando contatos para pessoa:', p.id);
      console.log('resolveContacts: contactService disponível:', !!contactService);
      console.log('resolveContacts: getContactsByPerson disponível:', !!contactService.getContactsByPerson);
      console.log('resolveContacts: URL da API:', `/api/v1/contacts/person/${p.id}`);
      
      // Teste se o contactService está funcionando
      console.log('resolveContacts: Testando chamada...');
      const response = await contactService.getContactsByPerson(p.id);
      console.log('resolveContacts: Resposta completa:', response);
      
      const contacts = response.data || [];
      console.log('resolveContacts: Contatos carregados do backend:', contacts);
      return contacts;
    } catch (error) {
      console.error('resolveContacts: Erro ao carregar contatos:', error);
      console.error('resolveContacts: Detalhes do erro:', error.response?.data);
      console.error('resolveContacts: Status do erro:', error.response?.status);
      console.error('resolveContacts: URL completa:', error.config?.url);
      return [];
    }
  };
  
  return (
    <FormPageShell>
      <div className="space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}
        {error && (
          <div className="alert alert-danger rounded-xl border border-red-200 text-red-800">
            {t('common:loadError')}
          </div>
        )}

        <PageHeader
          title={t("personsTitle")}
          description="Cadastro de pessoas físicas e contatos vinculados."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Identidade', to: '/identity/persons' },
            { label: 'Pessoas' },
          ]}
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Can permission={P.IDENTITY_ACCESS}>
              <button type="button" className="btn btn-outline shadow-sm">
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
            </Can>
            <Can permission={P.IDENTITY_ACCESS}>
              <button type="button" onClick={handleCreatePerson} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
                <Plus className="h-4 w-4" />
                Nova pessoa
              </button>
            </Can>
          </div>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <PersonFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando {persons.length} de {filteredPersons.length} pessoas
              </div>
            </div>

            {viewMode === 'table' ? (
              <PersonTable
                persons={persons}
                pagination={pagination}
                onView={handleViewPerson}
                onEdit={handleEditPerson}
                onDelete={handleDeletePerson}
                getStatusBadge={getStatusBadge}
                getPersonTypeBadge={getPersonTypeBadge}
                resolveDocument={resolveDocument}
                resolvePersonType={resolvePersonType}
                onCreate={handleCreatePerson}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {persons.map((person) => (
            <div key={person.id} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{person.name}</h3>
                      <p className="text-sm text-gray-600">
                        {resolvePersonType(person) === 'individual' ? (person.cpf || person.document) : (person.cnpj || person.document)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(resolveIsActive(person))}
                    {getPersonTypeBadge(resolvePersonType(person))}
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-700">
                    <strong>Documento:</strong> {resolveDocument(person)}
                  </div>
                  <div className="text-sm text-gray-700">
                    <strong>Endereço:</strong> {person.address}, {person.city}/{person.state}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Criado em:</span>
                    <span className="text-gray-900">
                      {new Date(resolveCreatedAt(person)).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleViewPerson(person)}
                    className="btn btn-outline btn-sm flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </button>
                  <Can permission={P.IDENTITY_ACCESS}>
                    <button
                      type="button"
                      onClick={() => handleEditPerson(person)}
                      className="btn btn-outline btn-sm flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                  </Can>
                </div>
              </div>
            </div>
          ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <PersonModal
          person={selectedPerson}
          onClose={() => setShowModal(false)}
          resolveContacts={resolveContacts}
          onSubmit={async (data) => {
            try {
              if (selectedPerson?.id) {
                await updateMutation.mutateAsync({ id: selectedPerson.id, data: transformPayload(data) });
                await saveContacts(selectedPerson.id, data.contacts);
              } else {
                if (!data.email?.trim()) {
                  toast.error(t('inviteEmailRequired'));
                  return;
                }
                if (!formatCpf(data.cpf)) {
                  toast.error('Informe um CPF válido com 11 dígitos.');
                  return;
                }
                const body = {
                  person: personToInvitePayload(data),
                  invite_email: data.email.trim(),
                  login_with: 'email',
                };
                let inviteRes;
                try {
                  ({ data: inviteRes } = await identityService.createPersonWithAccessInvite(body));
                } catch (inviteErr) {
                  const created = await identityService.createPerson(personToInvitePayload(data));
                  inviteRes = { person: created.data, email_sent: false, message: userFacingError(inviteErr, 'saveError') };
                }
                const pid = inviteRes?.person?.id;
                if (pid && data.contacts?.length > 0) {
                  await saveContacts(pid, data.contacts);
                }
                queryClient.invalidateQueries({ queryKey: ['persons'] });
                if (inviteRes?.email_sent) {
                  toast.success(t('personCreatedInvite'));
                } else {
                  toast.success(t('personCreatedActive'));
                  if (inviteRes?.message) toast(inviteRes.message, { icon: 'ℹ️', duration: 6000 });
                }
              }
              setShowModal(false);
            } catch (error) {
              toast.error(userFacingError(error, 'saveError'));
            }
          }}
        />
      )}
    </FormPageShell>
  );
};

export default Persons;
